// User-facing messages and labels
export const MESSAGES = {
  // App title and descriptions
  APP_TITLE: 'NGAP Kiné - Simulateur de cotations',
  APP_SUBTITLE: 'Trouvez rapidement les cotations de vos actes de kinésithérapie selon le référentiel NGAP en vigueur',
  
  // Loading states
  LOADING: {
    DATA: 'Chargement des données NGAP...',
    CALCULATING: 'calculating...'
  },
  
  // Error messages
  ERRORS: {
    CONNECTION: 'Erreur de connexion',
    CONNECTION_DETAIL: 'Impossible de charger les données NGAP. Vérifiez votre connexion internet.',
    LOADING: 'Erreur de chargement',
    LOADING_DETAIL: 'Une erreur inattendue s\'est produite lors du chargement des données.',
    UNEXPECTED: 'Une erreur s\'est produite',
    UNEXPECTED_DETAIL: 'Nous nous excusons pour ce désagrément. L\'application a rencontré une erreur inattendue.',
    COPY: 'Erreur lors de la copie:'
  },
  
  // Success messages
  SUCCESS: {
    DATA_LOADED: 'Données chargées',
    DATA_LOADED_DETAIL: 'actes NGAP disponibles',
    ACT_ADDED: 'Nouvel acte ajouté',
    ACT_ADDED_DETAIL: 'a été ajouté à la base de données',
    ACT_UPDATED: 'Acte mis à jour',
    ACT_UPDATED_DETAIL: 'a été modifié'
  },
  
  // Warning messages
  WARNINGS: {
    ACT_DELETED: 'Acte supprimé',
    ACT_DELETED_DETAIL: 'Un acte a été retiré de la base de données'
  },
  
  // Buttons and actions
  BUTTONS: {
    RETRY: 'Réessayer',
    RELOAD: 'Recharger la page',
    CLEAR: 'Effacer',
    CLOSE: 'Fermer',
    COPY: 'Copier',
    SHARE: 'Partager',
    COPIED: 'Copié !',
    ACTIVE: 'Actif'
  },
  
  // Form labels
  LABELS: {
    SEARCH_PLACEHOLDER: 'Rechercher un acte, un code NGAP...',
    LETTER_KEY: 'Codage',
    ALL_LETTER_KEYS: 'Toutes les codages',
    THEME: 'Thème',
    ALL_THEMES: 'Tous les thèmes',
    REEDUCATION: 'Type de rééducation',
    ALL_REEDUCATIONS: 'Tous les types de rééducation',
    REFERENTIEL: 'Référentiel',
    COTATION: 'Cotation (€)',
    MIN: 'Min',
    MAX: 'Max',
    PATIENTS_COUNT: 'Nombre de patients par jour :',
    PATIENT: 'patient',
    PATIENTS: 'patients'
  },
  
  // Sections and headings
  SECTIONS: {
    RECENT_SEARCHES: 'Recherches récentes',
    ADVANCED_FILTERS: 'Filtres avancés',
    RESULTS: 'Résultats',
    LETTER_KEY_INFO: 'Bon à savoir - Comprendre les lettres clés',
    REFERENTIEL_INFO: 'Informations référentiel',
    THEME_INFO: 'Informations sur le thème',
    REVALORIZATION: 'Revalorisations prévues',
    ORIGINAL_VALUE: 'Valeur de cotation d\'origine'
  },
  
  // Status and states
  STATUS: {
    REFERENTIEL: 'Référentiel',
    HORS_REFERENTIEL: 'Hors référentiel',
    CONDITIONS: 'Conditions',
    NO_COTATION: 'Pas de cotation',
    NOT_AVAILABLE: 'N/A',
    NON_DISPONIBLE: 'non disponible',
    NO_THEME: 'Aucun thème spécifié'
  },
  
  // Empty states
  EMPTY_STATES: {
    NO_RESULTS: 'Aucun résultat trouvé',
    NO_RESULTS_DETAIL: 'Essayez de modifier vos critères de recherche ou vos filtres'
  },
  
  // Pricing
  PRICING: {
    METROPOLE: 'Métropole:',
    DROM: 'DROM:',
    EURO: '€',
    PER_DAY: '/jour',
    POINTS: 'points'
  },
  
  // Pagination
  PAGINATION: {
    SHOWING: 'Affichage de',
    TO: 'à',
    OF: 'sur',
    RESULTS: 'résultats',
    FIRST_PAGE: 'Première page',
    PREVIOUS_PAGE: 'Page précédente',
    NEXT_PAGE: 'Page suivante',
    LAST_PAGE: 'Dernière page',
    GO_TO_PAGE: 'Aller à la page'
  },
  
  // Accessibility
  A11Y: {
    SEARCH_INSTRUCTIONS: 'Utilisez les flèches haut et bas pour naviguer dans les suggestions, Entrée pour sélectionner, Échap pour fermer.',
    SEARCH_LABEL: 'Rechercher des actes NGAP',
    CLEAR_SEARCH: 'Effacer la recherche',
    COPY_METROPOLE: 'Copier le tarif métropole',
    COPY_DROM: 'Copier le tarif DROM',
    FILTER_BY_LETTER: 'Filtrer par lettre clé',
    FILTER_BY_THEME: 'Filtrer par thème',
    FILTER_BY_REFERENTIEL: 'Filtrer par statut référentiel',
    COTATION_MIN: 'Cotation minimum',
    COTATION_MAX: 'Cotation maximum',
    SEARCH_FILTERS: 'Filtres de recherche',
    SEARCH_SUGGESTIONS: 'Suggestions de recherche',
    PAGINATION_NAV: 'Pagination des résultats'
  },
  
  // Share options
  SHARE: {
    TITLE: 'Partager cette cotation',
    EMAIL: 'Email',
    TWITTER: 'Twitter',
    LINKEDIN: 'LinkedIn',
    FACEBOOK: 'Facebook',
    COPY_LINK: 'Copier le lien'
  },
  
  // Referentiel details
  REFERENTIEL: {
    SESSIONS_BEFORE_APPROVAL: 'Nombre de séances avant accord préalable :',
    PRIOR_APPROVAL_REQUEST: 'Demande d\'accord préalable :',
    APPLICATION_CONDITIONS: 'Conditions d\'application :'
  }
} as const;

// Letter key information content
export const LETTER_KEY_INFO = {
  INTRO: 'Les lettres clés reflètent la réalité du terrain avec des indications qui permettent aux kinésithérapeutes de les mémoriser plus facilement :',
  
  MAIN_LETTERS: {
    TITLE: 'Lettres principales :',
    RA: 'rachis',
    AMP: 'amputation', 
    ARL: 'affections respiratoires',
    RAV: 'affections vasculaires'
  },
  
  SECOND_POSITION: {
    TITLE: 'En 2ᵉ position de la lettre-clé :',
    S: 'membre supérieur',
    I: 'membre inférieur'
  },
  
  THIRD_POSITION: {
    TITLE: 'En 3ᵉ position de la lettre-clé :',
    C: 'suite de chirurgie (ou O : opération)',
    M: 'médical'
  },
  
  EXAMPLES: {
    TITLE: 'Voici deux exemples :',
    RIM: {
      DESCRIPTION: 'Rééducation du Membre Inférieur non opéré soumis à référentiel.',
      EXPLANATION: '→ I = membre Inférieur, M = Médical'
    },
    VSC: {
      DESCRIPTION: 'Rééducation du membre Supérieur opéré non soumis à référentiel.',
      EXPLANATION: '→ S = Supérieur, C = Chirurgie'
    }
  }
} as const;